import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ShieldCheck, KeyRound, Monitor, Trash2, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { DetailField } from '@/components/common/DetailField';
import { FormField } from '@/components/common/FormField';
import { ErrorState } from '@/components/common/ErrorState';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/common/StatusBadge';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { authService } from '@/services/auth';
import { session } from '@/lib/session';
import { ApiError } from '@/lib/errors';
import { fmt } from '@/lib/format';
import {
  isOnCanonicalLoginOrigin,
  redirectToPlatformLogin,
} from '@/lib/platform-login';

export function ProfilePage() {
  const me = useCurrentUser();
  const navigate = useNavigate();

  if (!me) {
    return (
      <div className="space-y-6">
        <PageHeader title="My profile" />
        <ErrorState error={new Error('No active session')} />
      </div>
    );
  }

  const onSignOut = async () => {
    try {
      await authService.logout();
    } catch {
      /* swallow — logout should always succeed locally */
    }
    session.clear();
    toast.success('Signed out');
    if (!isOnCanonicalLoginOrigin()) {
      redirectToPlatformLogin();
      return;
    }
    navigate('/login', { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Settings"
        title="My profile"
        description="Account, security, and active sessions."
        actions={
          <Button variant="outline" onClick={onSignOut}>
            Sign out
          </Button>
        }
      />

      {/* Identity */}
      <SectionCard title="Account">
        <div className="grid grid-cols-2 gap-4">
          <DetailField label="Full name" value={me.fullName} />
          <DetailField label="Email" value={me.email} />
          <DetailField label="Phone" value={me.phone} />
          <DetailField label="Role" value={<Badge variant="muted" className="capitalize">{me.role}</Badge>} />
          <DetailField label="Tier" value={<Badge variant="muted" className="font-mono text-[10px]">{me.tier}</Badge>} />
          <DetailField
            label="Tenant"
            value={
              <span className="space-y-0.5">
                <span className="block font-medium">{me.tenantName}</span>
                <span className="block font-mono text-[11px] text-muted-foreground">{me.tenantCode}</span>
              </span>
            }
          />
          {me.organizationName && (
            <DetailField label="Organization" value={me.organizationName} />
          )}
          {me.lastLoginAt && (
            <DetailField label="Last sign-in" value={fmt.relative(me.lastLoginAt)} />
          )}
        </div>
      </SectionCard>

      <ChangePasswordSection />
      <MfaSection mfaEnrolled={me.mfaEnrolled} />
      <SessionsSection />
    </div>
  );
}

// ── Sections ────────────────────────────────────────────────────────────

function ChangePasswordSection() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      authService.changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: () => {
      toast.success('Password updated. Other sessions signed out.');
      setCurrent('');
      setNext('');
      setConfirm('');
    },
  });

  const apiError = mutation.error instanceof ApiError ? mutation.error : null;
  const passwordError = (apiError?.details as { newPassword?: string } | undefined)?.newPassword;
  const mismatch = confirm.length > 0 && next !== confirm;

  return (
    <SectionCard
      title={<span className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-muted-foreground" /> Password</span>}
      description="Use a unique, strong password. Other sessions will be signed out."
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (next !== confirm) return;
          mutation.mutate();
        }}
        className="space-y-4 max-w-sm"
      >
        <FormField label="Current password" htmlFor="current" required>
          <Input id="current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
        </FormField>
        <FormField label="New password" htmlFor="next" required error={passwordError}>
          <Input id="next" type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" required />
        </FormField>
        <FormField label="Confirm new password" htmlFor="confirm-pw" required error={mismatch ? "Passwords don't match" : undefined}>
          <Input id="confirm-pw" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />
        </FormField>
        {apiError && !passwordError && (
          <div className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-[12px] text-destructive">
            {apiError.message}
          </div>
        )}
        <Button type="submit" disabled={mutation.isPending || next.length < 12 || mismatch}>
          {mutation.isPending ? 'Updating…' : 'Update password'}
        </Button>
      </form>
    </SectionCard>
  );
}

function MfaSection({ mfaEnrolled }: { mfaEnrolled: boolean }) {
  return (
    <SectionCard
      title={<span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-muted-foreground" /> Two-factor authentication</span>}
      description="Add a second factor to your sign-in. Recommended for everyone, required for admins."
    >
      <div className="flex items-center justify-between gap-4">
        <div className="text-[13px]">
          {mfaEnrolled ? (
            <>
              <StatusBadge status="active" label="Enabled" /> <span className="ml-2 text-muted-foreground">— signed-in sessions verified MFA.</span>
            </>
          ) : (
            <>
              <StatusBadge status="inactive" label="Not enabled" /> <span className="ml-2 text-muted-foreground">— set up an authenticator app.</span>
            </>
          )}
        </div>
        <Button variant={mfaEnrolled ? 'outline' : 'default'} disabled>
          {mfaEnrolled ? 'Disable…' : 'Set up'}
        </Button>
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">
        Enrollment + recovery-code surface lands in the next FE iteration once the runtime ships
        <code className="mx-1 rounded bg-muted px-1 font-mono text-[10px]">/auth/mfa/enroll</code>.
      </p>
    </SectionCard>
  );
}

function SessionsSection() {
  const qc = useQueryClient();
  const sessions = useQuery({
    queryKey: ['auth', 'sessions'],
    queryFn: () => authService.listSessions(),
    retry: false,
  });

  const revoke = useMutation({
    mutationFn: (sid: string) => authService.revokeSession(sid),
    onSuccess: () => {
      toast.success('Session revoked');
      qc.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
  });

  const revokeOthers = useMutation({
    mutationFn: () => authService.revokeOtherSessions(),
    onSuccess: (res) => {
      toast.success(
        res.revoked === 0
          ? 'No other sessions to revoke'
          : `Signed out ${res.revoked} other ${res.revoked === 1 ? 'session' : 'sessions'}`,
      );
      qc.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not revoke sessions'),
  });

  const otherCount = (sessions.data?.items ?? []).filter((s) => !s.isCurrent).length;

  return (
    <SectionCard
      title={<span className="flex items-center gap-2"><Monitor className="h-4 w-4 text-muted-foreground" /> Active sessions</span>}
      description="Devices and browsers signed in to your account."
      actions={
        otherCount > 0 ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => revokeOthers.mutate()}
            disabled={revokeOthers.isPending}
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out other devices
          </Button>
        ) : undefined
      }
    >
      {sessions.error ? (
        <ErrorState error={sessions.error} onRetry={() => sessions.refetch()} />
      ) : sessions.isLoading ? (
        <div className="py-3 text-sm text-muted-foreground">Loading sessions…</div>
      ) : (
        <ul className="-my-2 divide-y divide-border/60">
          {(sessions.data?.items ?? []).map((s) => (
            <li key={s.sid} className="flex items-start justify-between gap-4 py-3">
              <div>
                <div className="text-sm font-medium text-foreground">
                  {s.userAgent ?? 'Unknown device'}
                  {s.isCurrent && <Badge variant="muted" className="ml-2 text-[10px]">This device</Badge>}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {[s.city, s.country].filter(Boolean).join(', ') || s.ipAddress || '—'}
                  {' · '}signed in {fmt.relative(s.createdAt)}
                  {' · '}last active {fmt.relative(s.lastUsedAt)}
                </div>
              </div>
              {!s.isCurrent && (
                <Button variant="ghost" size="sm" onClick={() => revoke.mutate(s.sid)} disabled={revoke.isPending}>
                  <Trash2 className="h-3.5 w-3.5" />
                  Revoke
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
